/**
 * Locked-module "Upgrade" teaser — the shared pieces.
 *
 * A module the school switched off is HIDDEN for teachers, parents, students
 * and staff, and shown locked to ADMIN / PRINCIPAL / CHAIRMAN / DIRECTOR
 * (`ModuleAccess.visibility(...) === "locked"`). Every locked surface uses
 * these, so the copy and the screen-reader text are the same everywhere:
 *   visible:  lock icon + "Upgrade"
 *   spoken:   "locked — not in your plan"
 * A locked row is still a real link: it opens the teaser page
 * (`components/setup/require-entitlement.tsx`), which renders no module code
 * and therefore fetches nothing.
 */
import { Link } from "react-router-dom";
import { LockFill } from "@/components/icons/solid-extra-icons";
import { moduleByKey } from "@campushubai/shared";
import "./module-lock.css";

export const MODULE_LOCKED_SR_TEXT = "locked — not in your plan";
export const MODULE_UPGRADE_COPY = "Available on a higher plan — contact CampusHub";
export const MODULE_UPGRADE_CONTACT_HREF = "mailto:hello@bytego.ai?subject=Upgrade%20my%20CampusHub%20plan";

/** Human label for a catalogue key ("transport" -> "Transport"); falls back to the key. */
export function moduleLabelFor(key: string | null | undefined): string {
  if (!key) return "This module";
  return moduleByKey(key)?.label ?? key;
}

export function ModuleLockChip({ onDark = false, label = "Upgrade", iconOnly = false }: { onDark?: boolean; label?: string; iconOnly?: boolean }) {
  const cls = ["ch-lock-chip", onDark ? "ch-lock-chip--on-dark" : "", iconOnly ? "ch-lock-chip--icon" : ""].filter(Boolean).join(" ");
  return (
    <span className={cls} title={iconOnly ? `${label} — ${MODULE_LOCKED_SR_TEXT}` : undefined}>
      <LockFill size={11} aria-hidden />
      {!iconOnly && <span aria-hidden>{label}</span>}
      <span className="sr-only">{MODULE_LOCKED_SR_TEXT}</span>
    </span>
  );
}

/** A locked tile for a dashboard module hub / quick-action grid. Opens the teaser page. */
export function LockedModuleTile({ label, href }: { label: string; href: string }) {
  return (
    <Link to={href} className="ch-lock-tile" data-locked-module="">
      <LockFill size={16} aria-hidden />
      <span className="ch-lock-tile__name">{label}</span>
      <ModuleLockChip />
    </Link>
  );
}
